import {
  CloseIcon,
  MicIcon,
  MicOffIcon,
  SpinnerIcon,
  StopIcon,
} from "@/components/icons";
import { Button } from "@/components/ui/button";

import { CHAT_TEXTS as TEXTS } from "../texts.ts";
import type { UseVoiceRecorderResult } from "../voice/use-voice-recorder.ts";

/**
 * Los controles del dictado, dentro de la fila del compositor.
 *
 * No saben grabar ni transcribir: leen el estado del hook y dibujan. El
 * estado vive arriba porque el mensaje de error va FUERA del formulario, y
 * partirlo en dos componentes es más barato que hacer que la caja de escribir
 * crezca hacia arriba.
 */
export function VoiceControls({
  voice,
  disabled,
}: {
  voice: UseVoiceRecorderResult;
  disabled: boolean;
}) {
  if (voice.state === "unsupported") {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled
        title={TEXTS.voice.unsupportedTitle}
        aria-label={TEXTS.voice.unsupportedLabel}
      >
        <MicOffIcon />
      </Button>
    );
  }

  if (voice.state === "recording") {
    return (
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          title={TEXTS.voice.discardTitle}
          aria-label={TEXTS.voice.cancelLabel}
          onClick={voice.cancel}
        >
          <CloseIcon />
        </Button>

        <div
          className="flex items-center gap-2 rounded-full bg-destructive/10 py-1 pr-1 pl-2.5 text-destructive"
          role="status"
        >
          <LevelMeter level={voice.level} />
          <span className="font-mono text-xs tabular-nums">
            {formatSeconds(voice.seconds)}
          </span>
          {/* Lo que oye un lector de pantalla; las barras son decoración. */}
          <span className="sr-only">{TEXTS.voice.recording}</span>
          <Button
            type="button"
            size="icon-sm"
            className="rounded-full"
            title={TEXTS.voice.stopTitle}
            aria-label={TEXTS.voice.stopLabel}
            onClick={voice.stop}
          >
            <StopIcon />
          </Button>
        </div>
      </div>
    );
  }

  if (voice.state === "transcribing" || voice.state === "starting") {
    const label =
      voice.state === "transcribing"
        ? TEXTS.voice.transcribing
        : TEXTS.voice.requestingPermission;

    return (
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          title={TEXTS.voice.cancel}
          aria-label={TEXTS.voice.cancelLabel}
          onClick={voice.cancel}
        >
          <CloseIcon />
        </Button>
        <div
          className="flex items-center gap-1.5 py-2 pr-2 text-sm text-muted-foreground"
          role="status"
        >
          <SpinnerIcon className="size-4 animate-spin motion-reduce:animate-none" />
          <span>{label}</span>
        </div>
      </div>
    );
  }

  // `idle` y `error`: desde el error se vuelve a intentar con el mismo botón.
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      disabled={disabled}
      title={TEXTS.voice.dictate}
      aria-label={TEXTS.voice.dictate}
      onClick={voice.start}
    >
      <MicIcon />
    </Button>
  );
}

/** El mensaje de error del dictado, encima de la caja de escribir. */
export function VoiceStatus({ voice }: { voice: UseVoiceRecorderResult }) {
  if (voice.state === "error" && voice.error !== null) {
    return (
      <div
        className="mb-2 flex items-start gap-2 text-sm text-destructive"
        role="alert"
      >
        <p className="flex-1">{voice.error}</p>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={voice.dismissError}
        >
          {TEXTS.voice.understood}
        </Button>
      </div>
    );
  }

  /**
   * El aviso NO es un error y no se dibuja como tal: el dictado sigue, solo
   * ha cambiado de camino. Con `role="status"` se anuncia sin interrumpir, al
   * contrario que la alerta de arriba.
   */
  if (voice.notice !== null) {
    return (
      <p className="mb-2 text-sm text-muted-foreground" role="status">
        {voice.notice}
      </p>
    );
  }

  /**
   * Mientras hay texto provisional se dice CON PALABRAS que no es definitivo.
   *
   * La cursiva y el color atenuado no pueden ser la unica señal: hay quien no
   * distingue la cursiva de un vistazo, quien tiene el contraste reducido y
   * quien no ve la caja en absoluto.
   */
  if (voice.interim !== "") {
    return (
      <p className="mb-2 text-sm text-muted-foreground">
        {TEXTS.voice.interimNotice}
      </p>
    );
  }

  return null;
}

/**
 * La prueba de que se está capturando audio, y no solo de que el botón está
 * pulsado: las barras siguen la voz. Con `prefers-reduced-motion` se quedan
 * quietas a media altura -- el cronómetro ya dice que la grabación sigue.
 */
function LevelMeter({ level }: { level: number }) {
  return (
    <span className="flex h-4 items-center gap-0.5" aria-hidden="true">
      {BAR_WEIGHTS.map((weight, index) => (
        <span
          key={index}
          className="w-0.5 rounded-full bg-current transition-transform duration-75 motion-reduce:transition-none motion-reduce:[transform:scaleY(0.5)]"
          style={{
            height: "100%",
            // Un mínimo visible siempre: cuatro rayas invisibles en los
            // silencios parecerían que se ha caído la grabación.
            transform: `scaleY(${String(Math.max(0.15, Math.min(1, level * weight)))})`,
          }}
        />
      ))}
    </span>
  );
}

/** Las de en medio reaccionan más: así parece una onda y no un ecualizador. */
const BAR_WEIGHTS = [0.7, 1.15, 1.4, 1.15, 0.7];

const formatSeconds = (total: number): string => {
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, "0")}`;
};
